import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { fetchGoldUsd } from '../utils/goldUsdApi';
import { fmtSignedCorr, pickCorr, regime, situationAtPoint } from '../utils/goldUsdSignal';
import GoldUsdHelp from './GoldUsdHelp';
import './BondDashboard.css';
import './GoldUsdPanel.css';
import './StockBondReturnsPanel.css';

const RANGE_OPTIONS = ['6M', '1Y', '2Y', '5Y', 'All'];
const DEFAULT_RANGE = '5Y';
const USD_MODE_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'dxy', label: 'DXY' },
  { id: 'eurusd_proxy', label: 'EUR/USD proxy' },
];
const CORR_MIN = -1;
const CORR_MAX = 1;
const SCALE_MIN_WIDTH = 72;

const COLOR_GOLD = '#B8860B';
const COLOR_USD = '#2E5C3E';
const CORR_COLORS = { 30: '#A16D17', 60: '#3A17A1', 90: '#1776A1' };

function toChartData(bars) {
  return (bars || []).map((b) => ({ time: b.time, value: Number(b.value) }));
}

function crosshairTimeString(time) {
  if (time == null) return null;
  if (typeof time === 'string') return time.slice(0, 10);
  if (typeof time === 'object' && time.year != null) {
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${time.year}-${m}-${d}`;
  }
  return String(time).slice(0, 10);
}

function formatChartDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${d}, ${y}`;
}

function GoldUsdPanel() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const payloadRef = useRef(null);

  const [range, setRange] = useState(DEFAULT_RANGE);
  const [usdMode, setUsdMode] = useState('auto');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);

  const situation = payload?.situation;
  const latest = payload?.latest;
  const crossRead = payload?.cross_read;

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGoldUsd({ range, usdMode });
      setPayload(data);
    } catch (err) {
      setError(err.message || 'Failed to load Gold vs USD');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [range, usdMode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!payload?.bars || !chartContainerRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      leftPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.2, bottom: 0.14 },
      },
      rightPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 360,
    });

    const goldSeries = chart.addSeries(LineSeries, {
      color: COLOR_GOLD,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: true,
      title: 'Gold',
    });
    const usdSeries = chart.addSeries(LineSeries, {
      color: COLOR_USD,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'USD',
    });

    const corrSeries = {};
    for (const w of [30, 60, 90]) {
      corrSeries[w] = chart.addSeries(LineSeries, {
        color: CORR_COLORS[w],
        lineWidth: 1.5,
        lineStyle: 1,
        priceScaleId: 'right',
        priceLineVisible: false,
        lastValueVisible: false,
        title: `${w}d`,
      });
    }
    const zeroSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'right',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const { bars: b } = payload;
    goldSeries.setData(toChartData(b.gold_indexed));
    usdSeries.setData(toChartData(b.usd_indexed));
    corrSeries[30].setData(toChartData(b.corr30));
    corrSeries[60].setData(toChartData(b.corr60));
    corrSeries[90].setData(toChartData(b.corr90));
    const zeroBase = b.corr90?.length ? b.corr90 : b.corr30;
    if (zeroBase?.length) {
      zeroSeries.setData(zeroBase.map((p) => ({ time: p.time, value: 0 })));
    }

    try {
      chart.priceScale('right').applyOptions({ autoScale: false, scaleMargins: { top: 0.08, bottom: 0.08 } });
      chart.priceScale('right').setVisibleRange({ from: CORR_MIN, to: CORR_MAX });
    } catch {
      /* empty */
    }
    chart.timeScale().fitContent();

    const onCrosshairMove = (param) => {
      if (!param.time || param.point == null) {
        setHoverTip(null);
        return;
      }
      const time = crosshairTimeString(param.time);
      const p = payloadRef.current?.bars;
      if (!p) {
        setHoverTip(null);
        return;
      }
      const c30 = pickCorr(p.corr30, time);
      const c60 = pickCorr(p.corr60, time);
      const c90 = pickCorr(p.corr90, time);
      if (c30 == null && c90 == null) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: param.point.x,
        y: param.point.y,
        time,
        ...situationAtPoint(c30, c60, c90),
      });
    };
    chart.subscribeCrosshairMove(onCrosshairMove);
    chartRef.current = chart;

    const disconnect = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height: height || 360 });
    });

    return () => {
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      disconnect();
      chart.remove();
      chartRef.current = null;
      setHoverTip(null);
    };
  }, [payload]);

  const r90 = useMemo(() => regime(latest?.correlation?.d90), [latest]);

  return (
    <section id="macro-gold-usd" className="bond-dash-panel gold-usd-panel">
      <div className="bond-dash-header gold-usd-header">
        <div>
          <h3 className="bond-dash-title">Gold vs USD — normal link or stress haven?</h3>
          <p className="bond-dash-subtitle">
            {payload?.usd_method_note || 'USD-priced gold vs dollar strength.'}
            {payload?.diagnostics?.aligned_from && payload?.diagnostics?.aligned_to
              ? ` · ${payload.diagnostics.aligned_from} → ${payload.diagnostics.aligned_to}`
              : ''}
            . Hover for regime at that date.
          </p>
          <GoldUsdHelp />
        </div>
        {latest && (
          <div className="btc-nasdaq-legend">
            <span className="gold-usd-legend-gold">
              {payload?.gold_symbol} {latest.gold_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className="gold-usd-legend-usd">
              USD {latest.usd_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className={`gold-usd-regime gold-usd-regime--${r90.cssClass}`}>
              {r90.emoji} {r90.label} (90d)
            </span>
          </div>
        )}
      </div>

      {situation && (
        <div className="btc-nasdaq-signal-block">
          <span
            className={`btc-nasdaq-signal-headline gold-usd-regime--${situation.primary_css_class}`}
          >
            {situation.headline}
            {situation.stress_signal ? ' ⚠️ 4+ week stress streak' : ''}
          </span>
          <p className="btc-nasdaq-signal-detail">{situation.detail}</p>
          <p className="btc-nasdaq-signal-tooltip-line">{situation.tooltip_line}</p>
        </div>
      )}

      {crossRead?.interpretation && (
        <div className="gold-usd-cross-read">
          <div className="gold-usd-cross-read-title">Reading with BTC–Nasdaq (90d)</div>
          <p>
            BTC {crossRead.btc_band === 'high' ? 'high beta' : 'low/decoupled'} · Gold{' '}
            {crossRead.gold_regime?.label ?? crossRead.gold_band} — {crossRead.interpretation}
          </p>
        </div>
      )}

      <div className="stock-bond-toolbar">
        <span className="stock-bond-range-label">Range</span>
        {RANGE_OPTIONS.map((label) => (
          <button
            key={label}
            type="button"
            className={`stock-bond-range-btn${range === label ? ' active' : ''}`}
            onClick={() => setRange(label)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
        <span className="stock-bond-range-label">USD leg</span>
        {USD_MODE_OPTIONS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`stock-bond-range-btn${usdMode === id ? ' active' : ''}`}
            onClick={() => setUsdMode(id)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="stock-bond-charts">
        <div className="stock-bond-corr-header">
          <span className="stock-bond-corr-title">Rolling correlation (log returns)</span>
          {latest?.correlation && (
            <div className="stock-bond-corr-legend">
              <span style={{ color: CORR_COLORS[30] }}>30d {fmtSignedCorr(latest.correlation.d30)}</span>
              <span style={{ color: CORR_COLORS[60] }}>60d {fmtSignedCorr(latest.correlation.d60)}</span>
              <span style={{ color: CORR_COLORS[90] }}>90d {fmtSignedCorr(latest.correlation.d90)}</span>
            </div>
          )}
        </div>

        {loading && (
          <div className="gold-usd-chart-placeholder">Loading Gold vs USD…</div>
        )}

        {!loading && error && (
          <div className="gold-usd-chart-placeholder gold-usd-chart-placeholder--error">
            <div>
              <strong>{error}</strong>
              <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>
                Backfill GLD and USD data, then restart the investing API (port 5001):
                <br />
                <code>python3 -m investing.backend --benchmarks-only</code>
                <br />
                <code>python3 -m investing.backend --fx-only</code>
              </p>
            </div>
          </div>
        )}

        {!loading && !error && payload?.bars && (
          <div className="stock-bond-chart-wrap">
            <span className="stock-bond-axis-label stock-bond-axis-label--left">Indexed (100)</span>
            <div ref={chartContainerRef} className="stock-bond-chart stock-bond-chart-combined" />
            {hoverTip && (
              <div
                className="stock-bond-chart-tooltip"
                style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
                role="tooltip"
              >
                <div className="stock-bond-chart-tooltip-date">{formatChartDate(hoverTip.time)}</div>
                <div className={`stock-bond-chart-tooltip-regime gold-usd-regime--${hoverTip.primaryCss}`}>
                  {hoverTip.r90?.emoji} {hoverTip.headline}
                </div>
                <p className="stock-bond-chart-tooltip-body">{hoverTip.detail}</p>
              </div>
            )}
            <span className="stock-bond-axis-label stock-bond-axis-label--right">Correlation</span>
          </div>
        )}
      </div>
    </section>
  );
}

export default GoldUsdPanel;
